import { EmployeeRoleId } from '@previo/codebooks'
import { useUserStore } from '~/store/user'
import { UserTypeId } from '~/http/user/User'

export const usePermissionChecker = () => {
  const userStore = useUserStore()

  const isRoot = (): boolean => {
    return userStore.user.userTypeId === UserTypeId.ROOT
  }

  const canViewEmployeeModule = (module: CrmModule): boolean => {
    return userStore.user.employee?.modules.includes(module) ?? false
  }

  const hasAnyEmployeeModule = (modules: CrmModule[]): boolean => {
    return modules.some((m) => canViewEmployeeModule(m))
  }

  const isEmployeeModuleReadOnly = (module: CrmModule): boolean => {
    return userStore.user.employee?.readOnlyModules?.includes(module) ?? false
  }

  const canEditEmployeeModule = (module: CrmModule): boolean => {
    return canViewEmployeeModule(module) && !isEmployeeModuleReadOnly(module)
  }

  const isEmployeeAdmin = (): boolean => {
    return userStore.user.employee?.roleIds.includes(EmployeeRoleId.ADMIN) ?? false
  }

  const isEmployeeSalesDirector = (): boolean => {
    return userStore.user.employee?.roleIds.includes(EmployeeRoleId.SALES_DIRECTOR) ?? false
  }

  return {
    isRoot,
    CrmModule,
    canViewEmployeeModule,
    hasAnyEmployeeModule,
    isEmployeeModuleReadOnly,
    canEditEmployeeModule,
    isEmployeeAdmin,
    isEmployeeSalesDirector,
  }
}

export enum CrmModule {
  SALES_CASES = 'sales.salesCases',
  SALES_COMMISSIONS = 'sales.commissions',
  SALES_DOCUMENTS = 'sales.documents',
  SALES_CONTRACTS = 'sales.contracts',
  SALES_EVENTS = 'sales.events',
  SALES_SALES = 'sales.sales',
  SALES_DASHBOARD = 'sales.salesDashboard',
  PROPERTIES = 'properties',
  PROPERTIES_REVIEWS = 'properties.reviews',
  PROPERTIES_FRAUDS = 'properties.frauds',
  PROPERTIES_RESERVATIONS = 'properties.reservations',
  PROPERTIES_RECALL = 'properties.recall',
  PROPERTIES_MAP = 'properties.map',
  CONTACT_PERSONS = 'backoffice.contactPersons',
  OTA_PARTNERS = 'ota.partners',
  OTA_COOPERATION = 'ota.cooperation',
  OTA_CM = 'ota.channelManager',
  INVOICES = 'invoices',
  USERS = 'users',
  USERS_ACTIVITY = 'users.activity',
  EMPLOYEES = 'users.employees',
  API_USERS = 'users.apiUsers',
  INTEGRATION_PARTNERS = 'users.integrationPartners',
  USERS_ROLES = 'users.roles',
  USERS_GROUPS = 'users.groups',
  SALESMEN = 'users.salesmen',
  SYSTEM = 'system',
  SYSTEM_PREVIO_PAY = 'system.previoPay',
  SYSTEM_SITES = 'system.sites',
  SYSTEM_SPA_PROCEDURES = 'system.spaProcedures',
  SYSTEM_TIPBOX = 'system.tipbox',
  SYSTEM_FEEDBACK = 'system.feedback',
  SYSTEM_PMS_VERSION_OVERRIDES = 'system.pmsVersionOverrides',
}
